// icons
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import EmojiObjectsOutlinedIcon from '@mui/icons-material/EmojiObjectsOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import PollOutlinedIcon from '@mui/icons-material/PollOutlined';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import TourIcon from '@mui/icons-material/Tour';
import SummarizeIcon from '@mui/icons-material/Summarize';
import PersonIcon from '@mui/icons-material/Person';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import UpdateIcon from '@mui/icons-material/Update';
import InsightsIcon from '@mui/icons-material/Insights';
import AnalyticsOutlinedIcon from '@mui/icons-material/AnalyticsOutlined';
import AddChartIcon from '@mui/icons-material/Addchart';
import InventoryIcon from '@mui/icons-material/Inventory';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
// react
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { resetPackageList } from '../../features/Package/packageSlice';
import usePackageSelection from '../../modules/Accounts/components/PackageSelection/usePackageSelection';
import { logoutUser, refreshSession } from '../../features/Auth/authAction';
import { broadcastMessage } from '../../utils/broadcastEvents';
import useAuthorization from '../../hooks/useAuthorization';
import { openSnackbar } from '../../features/SnackBar/snackBarSlice';
import { startTour } from '../../features/TourGuide/tourGuideSlice';

function useAppMenu({ permissions }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(null);
  const { userInfo } = useAuthorization();

  const handleClick = (index) => {
    if (open === null || open !== index) {
      setOpen(index);
    } else {
      setOpen(null);
    }
  };

  const handleLogout = () => {
    const idToken = localStorage.getItem('idToken');
    const accessToken = localStorage.getItem('accessToken');
    const refreshToken = localStorage.getItem('refreshToken');
    broadcastMessage('LogoutUser');
    const tokens = {
      idToken,
      accessToken,
      refreshToken,
    };
    localStorage.removeItem('persona');
    dispatch(logoutUser(tokens));
  };

  const handleStopImpersonate = () => {
    const refreshToken = localStorage.getItem('refreshToken');
    dispatch(refreshSession({ refreshToken })).then((res) => {
      if (res.payload.status === 'SUCCESS') {
        navigate('/');
        localStorage.removeItem('activePackage');
        dispatch(resetPackageList());
        usePackageSelection();
        dispatch(openSnackbar({ open: true, message: 'Successfully stopped impersonating!', type: 'success' }));
      } else {
        handleLogout();
        dispatch(openSnackbar({ open: true, message: 'Failed to stop impersonating, logging out!', type: 'error' }));
      }
      localStorage.removeItem('persona');
    });
  };

  const handleStartTour = () => {
    if (location.pathname === '/dashboard') {
      dispatch(startTour());
    } else {
      navigate('/dashboard', { state: { startTour: true } });
    }
  };

  const handleExploreDocs = () => {
    window.open('https://doc.isvinsights.ai/', '_blank');
  };

  const isImpersonating = userInfo.access !== 'superAdmin' && userInfo.group === 'superAdmin';
  const isTourGuideDisabled = true; // userInfo.access === 'superAdmin' // disabled for now
  const isDocDisabled = userInfo.access === 'superAdmin'; // disabled for now
  const customFilterViewList = useSelector((state) => state.customFilterView?.data);

  const accountWatchList = customFilterViewList?.map((item) => ({
    id: item.id,
    name: item.name,
    link: `/account-list/${item.id}`,
  }));

  const bottomMenuItems = [
    {
      name: 'Tour Guide',
      link: '',
      Icon: TourIcon,
      permission: isTourGuideDisabled,
      onClick: handleStartTour,
    },
    {
      name: 'Documentation',
      link: '',
      Icon: SummarizeIcon,
      permission: isDocDisabled,
      onClick: handleExploreDocs,
    },
    {
      name: isImpersonating ? 'Stop Impersonating' : 'Profile',
      link: isImpersonating ? '' : '/my-profile',
      Icon: PersonIcon,
      permission: permissions?.manage,
      onClick: isImpersonating ? handleStopImpersonate : undefined,
    },
    {
      name: 'Logout',
      link: '',
      Icon: PowerSettingsNewIcon,
      onClick: handleLogout,
    },
  ];

  const appMenuItems = [
    {
      name: 'Dashboard',
      link: '/dashboard',
      Icon: DashboardOutlinedIcon,
    },
    {
      name: 'Accounts',
      Icon: BusinessOutlinedIcon,
      items: [
        {
          name: 'Active Accounts',
          link: '/account-list/all',
        },
        ...accountWatchList,
      ],
    },
    {
      name: 'Product Insights',
      Icon: EmojiObjectsOutlinedIcon,
      items: [
        {
          name: 'Feature Usage',
          link: '/feature-usage',
          Icon: PollOutlinedIcon,
        },
        {
          name: 'Geography',
          link: '/geographies',
          Icon: LanguageOutlinedIcon,
        },
        {
          name: 'Custom Object',
          link: '/custom-objects',
          Icon: ViewInArIcon,
        },
        {
          name: 'Engagement',
          link: '/active-users',
          Icon: TrendingUpIcon,
        },
        {
          name: 'Versions',
          link: '/versions',
          Icon: UpdateIcon,
        },
      ],
    },
    {
      name: 'Score Insights',
      link: '/score-insights',
      Icon: InsightsIcon,
    },
    {
      name: 'Log Analyzer',
      link: '/log-analyzer',
      Icon: AnalyticsOutlinedIcon,
    },
    {
      name: 'Setup',
      Icon: SettingsSuggestIcon,
      permission: 'write',
      items: [
        {
          name: 'Feature Manager',
          link: '/feature-manager',
          Icon: AddChartIcon,
        },
        {
          name: 'Feature Taxonomy',
          link: '/taxonomy',
          Icon: AccountTreeOutlinedIcon,
        },
        {
          name: 'KPI Scores',
          link: '/kpi-score-configuration',
          Icon: MonitorHeartIcon,
        },
        {
          name: 'User Manager',
          link: '/user-manager',
          Icon: ManageAccountsIcon,
        },
        {
          name: 'Package Manager',
          link: '/package-list',
          Icon: InventoryIcon,
        },
        {
          name: 'Connect PBO',
          link: '/connect-pbo',
          Icon: CloudDoneIcon,
        },
        {
          name: 'Configuration',
          link: '/configuration',
          Icon: BuildOutlinedIcon,
        },
      ],
    },
  ];

  const tenantMenu = [
    {
      name: 'Tenants',
      link: '/dashboard',
      Icon: SupervisorAccountIcon,
      access: 'superAdmin',
    },
  ];

  return {
    open,
    setOpen,
    handleClick,
    handleLogout,
    bottomMenuItems,
    appMenuItems,
    tenantMenu,
  };
}

export default useAppMenu;
